import { Component, Input } from '@angular/core'
import { closeJobSelectionDialog } from '../../../shared/utils/job-selection-dialog-utils'
import {
    PipelineType,
    SegmentationModel,
    SegmentationParameters,
    SegmentationInputItem,
    ModelInput,
} from '../../../shared/model/jobs'
import { JobApiService } from '../../../shared/services/job-api.service'
import { Observable, take } from 'rxjs'
import { StaticLayerConfig } from '../../../state/viewer/viewer.state'
import { Store } from '@ngrx/store'
import { selectImageLayers, selectLayers } from '../../../state/viewer/viewer.selectors'
import { ToasterService } from '../../../shared/services/toaster.service'
import { HttpErrorResponse } from '@angular/common/http'
import { environment } from '../../../../environments/environment'
import { Dialog } from '@angular/cdk/dialog'
import { SelectLayersDialogComponent, SelectLayersDialogData } from './select-layers-dialog/select-layers-dialog.component'

@Component({
    selector: 'app-auto-segmentation-selection',
    templateUrl: './auto-segmentation-selection.component.html',
    styleUrls: ['./auto-segmentation-selection.component.scss'],
})
export class AutoSegmentationSelectionComponent {
    @Input()
    siblingDatasets: string[] | never[]
    imageLayers$: Observable<StaticLayerConfig[]>

    models$: Observable<SegmentationModel[]>
    selectedModel: SegmentationModel | null = null

    /** Selected layers per model input (1–5 layers). One layer = no combination; 2+ = combined with max. */
    inputSelections: { [inputName: string]: StaticLayerConfig[] | null } = {}

    submitted: boolean = false
    runBatchJob: boolean = false

    /** User-provided suffix for the job issue title (preview: "{model display_name} - {this}"). */
    issueDisplayName: string = ''

    private readonly wikiUrl = environment.wikiUrl

    constructor(
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService,
        private readonly dialog: Dialog
    ) {
        this.imageLayers$ = this.store.select(selectImageLayers)
        this.models$ = this.jobApi.getSegmentationModels()
    }

    get modelInputs(): ModelInput[] {
        const channels = this.selectedModel?.input_channels
        return Array.isArray(channels) ? channels : []
    }

    get canStart(): boolean {
        if (!this.selectedModel || this.submitted) {
            return false
        }

        if (this.issueDisplayName.trim().length === 0) {
            return false
        }

        const inputs = this.selectedModel.input_channels
        if (!inputs || !Array.isArray(inputs) || inputs.length === 0) return false

        return inputs.every((input: ModelInput) => {
            const layers = this.inputSelections[input.name]
            return layers != null && layers.length >= 1
        })
    }

    /** Full job / layer label: "{model display_name} - {user suffix}" (trimmed). */
    get fullJobDisplayName(): string {
        if (!this.selectedModel) return ''
        return `${this.selectedModel.display_name} - ${this.issueDisplayName.trim()}`
    }

    cancel(): void {
        closeJobSelectionDialog()
    }

    startJob(): void {
        if (!this.canStart || !this.selectedModel) return

        const fullName = this.fullJobDisplayName
        this.store
            .select(selectLayers)
            .pipe(take(1))
            .subscribe(layers => {
                const existingNames = new Set(
                    (layers ?? [])
                        .map(l => l.name)
                        .filter((n): n is string => Boolean(n))
                )
                if (existingNames.has(fullName)) {
                    this.toaster.warning(
                        'A layer with this name already exists. Choose a different display name.',
                        'Duplicate layer name'
                    )
                    return
                }

                this.submitted = true

                const inputs = this.selectedModel!.input_channels
                const inputItems: SegmentationInputItem[] = Array.isArray(inputs)
                    ? inputs
                          .map((input: ModelInput) => {
                              const selected = this.inputSelections[input.name]
                              if (!selected || selected.length === 0) return null
                              const names = selected
                                  .map(l => l.name)
                                  .filter((n): n is string => Boolean(n))
                              if (names.length === 0) return null
                              const item: SegmentationInputItem = {
                                  input_name: input.name,
                                  layers: names,
                              }
                              if (names.length > 1) {
                                  item.combination_method = 'max'
                              }
                              return item
                          })
                          .filter((item): item is SegmentationInputItem => item !== null)
                    : []

                const parameters: SegmentationParameters = {
                    type: PipelineType.Segmentation,
                    model_id: this.selectedModel!.id,
                    batch: this.runBatchJob,
                    inputs: inputItems,
                    result_display_name: this.fullJobDisplayName,
                }

                this.jobApi.startSegmentationJob(parameters).subscribe({
                    next: () => closeJobSelectionDialog(),
                    error: (err: HttpErrorResponse) => {
                        this.toaster.httpError(err, 'Error starting job')
                        this.submitted = false
                    },
                })
            })
    }

    onModelSelected(model: SegmentationModel | null): void {
        this.selectedModel = model
        this.issueDisplayName = ''
        this.inputSelections = {}
        if (model?.input_channels && Array.isArray(model.input_channels)) {
            model.input_channels.forEach((input: ModelInput) => {
                this.inputSelections[input.name] = null
            })
        }
    }

    getLayersSummary(inputName: string): string {
        const layers = this.inputSelections[inputName]
        if (!layers || layers.length === 0) return 'No layers selected'
        return layers.map(l => l.name).filter(Boolean).join(', ')
    }

    openSelectLayersDialog(inputName: string): void {
        const currentSelection = this.inputSelections[inputName]

        const data: SelectLayersDialogData = {
            inputName,
            availableLayers$: this.imageLayers$,
            currentSelection: currentSelection && currentSelection.length > 0 ? currentSelection : null,
        }

        const dialogRef = this.dialog.open<StaticLayerConfig[] | null>(SelectLayersDialogComponent, {
            data,
            width: '500px',
        })

        dialogRef.closed.subscribe(result => {
            if (result !== undefined && result !== null) {
                this.inputSelections[inputName] = result
            }
        })
    }

    goToWiki(): void {
        window.open(`${this.wikiUrl}home/Working_with_jobs#h-2-cell-segmentation`, '_blank')
    }
}
